import { HUBSPOT_PORTAL_ID, isDev, isLocalhost, isProd } from 'v2/configs/constants'
import { logger } from 'v2/utils/logger'

/**
 * HubSpot tracking code (js.hs-scripts.com/<portalId>.js).
 *
 * The script records exactly one page view — the moment it loads — and then never
 * notices the URL again: `history.pushState` is invisible to it. So in this SPA
 * every route after the first has to be reported by hand with the
 * `setPath` + `trackPageView` pair HubSpot's tracking-code API prescribes.
 *
 * Everything goes through the `_hsq` queue rather than a loaded global: the array
 * is ours and exists before the script does, and HubSpot drains whatever it finds
 * on load. A call made during startup is therefore buffered, and one made while an
 * ad-blocker keeps the script from ever arriving is inert instead of throwing.
 */

/** HubSpot's own id for the loader tag, kept so the snippet stays recognisable. */
export const HUBSPOT_SCRIPT_ID = 'hs-script-loader'

/**
 * Query params HubSpot reads to assign a visit's original source. An ALLOWLIST, not
 * a denylist of secrets: console URLs carry single-use credentials
 * (`/set-password?token=`, `/complete-signup?token=`, `/cli/authorize?state=`) and a
 * denylist leaks the one param nobody thought to name. Anything not listed here is
 * dropped before HubSpot sees the URL.
 */
export const ATTRIBUTION_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'msclkid',
  'li_fat_id',
  'ttclid',
  'hsa_acc',
  'hsa_cam',
  'hsa_grp',
  'hsa_ad',
  'hsa_src',
  'hsa_tgt',
  'hsa_kw',
  'hsa_mt',
  'hsa_net',
  'hsa_ver',
  '_hsenc',
  '_hsmi',
]

/**
 * Collapses redirect chains. Landing on `/` bounces through `<Navigate>` to `/login`
 * or the default route within a frame or two, and each hop is a location change —
 * without the delay HubSpot would bank a page view for a URL nobody ever saw. Short
 * enough that a real click-through is still recorded on the page it landed on.
 */
export const PAGE_VIEW_DEBOUNCE_MS = 300

/** The shape both `window.location` and react-router's location satisfy. */
interface PathParts {
  pathname: string
  search: string
}

/**
 * The path the tracking code's own load-time page view should report. Pathname plus
 * the attribution params only — a deep link into the app otherwise hands HubSpot
 * whatever the query string was carrying.
 */
export const initialTrackedPath = ({ pathname, search }: PathParts): string => {
  const incoming = new URLSearchParams(search)
  const kept = new URLSearchParams()
  for (const key of ATTRIBUTION_PARAMS) {
    const value = incoming.get(key)
    if (value) kept.set(key, value)
  }
  const query = kept.toString()
  return query ? `${pathname}?${query}` : pathname
}

/**
 * Reports one SPA page view. No-op until `loadHubspotTracking` has created the
 * queue, so non-prod builds never accumulate pushes for a script that is not there.
 */
export const trackHubspotPageView = (path: string) => {
  const queue = window._hsq
  if (!queue) return
  try {
    queue.push(['setPath', path])
    queue.push(['trackPageView'])
  } catch (error) {
    logger.error(`trackHubspotPageView failed for path "${path}"`, error)
  }
}

let lastIdentifiedEmail = ''

export const resetHubspotIdentify = () => {
  lastIdentifiedEmail = ''
}

// Links this browser's page views to the contact; HubSpot only sends the identity with a page view.
export const identifyHubspotContact = (email: string) => {
  const queue = window._hsq
  if (!queue || email === lastIdentifiedEmail) return
  try {
    queue.push(['identify', { email }])
    queue.push(['trackPageView'])
    lastIdentifiedEmail = email
  } catch (error) {
    logger.error('identifyHubspotContact failed', error)
  }
}

/**
 * Creates the queue, seeds the current path so the tracking code's automatic
 * load-time page view lands on the right URL, then injects the script. Idempotent.
 *
 * Separate from the environment gate below because that gate is a build-time
 * constant — folded away in any non-prod bundle, and therefore not something a test
 * can flip.
 */
export const injectHubspotTracking = () => {
  if (document.getElementById(HUBSPOT_SCRIPT_ID)) return

  try {
    window._hsq = window._hsq || []
    window._hsq.push(['setPath', initialTrackedPath(window.location)])

    const script = document.createElement('script')
    script.id = HUBSPOT_SCRIPT_ID
    script.async = true
    script.defer = true
    script.src = `https://js.hs-scripts.com/${HUBSPOT_PORTAL_ID}.js`
    document.head.appendChild(script)
  } catch (error) {
    logger.error('injectHubspotTracking failed', error)
  }
}

/**
 * Deployed apps only, never a developer's machine. Dev and prod resolve to different
 * portals (see HUBSPOT_PORTAL_ID), so deployed-dev traffic stays out of the portal
 * that drives customer reports and contact timelines. Both flags are inlined at build
 * time, so a localhost bundle drops the loader entirely.
 */
export const loadHubspotTracking = () => {
  if ((!isProd && !isDev) || isLocalhost) return
  injectHubspotTracking()
}
